import { FC, useMemo } from 'react';
import { _notNil } from '@/littledash';
import Tooltip from '@/components/UI/Tooltip';
import type { AvatarColor } from '@/constants/colors';
import type { StudyUserSummaryV1, UserApiId } from '@/model/User.model';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { type OnlineUsersModel, useOnlineUsers } from '@/components/UI/DataTableV2/hooks/useOnlineUsers';
import { useRemoteSelectionInteraction } from '@/components/UI/DataTableV2/RemoteSelectionInteractionContext';
import { UserPresenceChip } from './UserPresenceChip';
import styles from './DataTableOnlineUsers.module.scss';

const VISIBLE_LIMIT = 5;

interface ResolvedUser {
  apiId: UserApiId;
  color: AvatarColor;
  name: string;
  isActive: boolean;
}

interface OverflowChipProps {
  users: ReadonlyArray<ResolvedUser>;
}

/** Static list of overflow users shown inside the `+N` tooltip. */
const OverflowList: FC<OverflowChipProps> = ({ users }) => (
  <ul className={styles.overflowList} data-testid="datatable-online-users__overflow-list">
    {users.map((user) => (
      <li key={user.apiId} className={styles.overflowRow}>
        <UserPresenceChip name={user.name} color={user.color} isActive={user.isActive} showTooltip={false} />
        <span>{user.name}</span>
      </li>
    ))}
  </ul>
);

/** Leftmost `+N` chip whose tooltip reveals the overflow users. */
const OverflowChip: FC<OverflowChipProps> = ({ users }) => (
  <Tooltip render={<OverflowList users={users} />}>
    <span className={styles.overflowChip} data-testid="datatable-online-users__overflow">
      +{users.length}
    </span>
  </Tooltip>
);

const resolveUsers = (
  online: OnlineUsersModel,
  studyUsers: ReadonlyMap<UserApiId, StudyUserSummaryV1>
): Array<ResolvedUser> => {
  const resolved: Array<ResolvedUser> = [];
  online.byId.forEach(({ apiId, color }) => {
    const studyUser = studyUsers.get(apiId);
    if (_notNil(studyUser)) {
      resolved.push({ apiId, color, isActive: online.activeUserIds.has(apiId), name: studyUser.name });
    }
  });
  return resolved;
};

/**
 * Renders a row of avatar chips representing the users currently viewing this
 * datatable. The hook returns users in server order (most-recently-engaged
 * first); we display up to 5 of those reversed so the row reads
 * oldest-of-visible → newest left-to-right, with a leftmost `+N` chip listing
 * the rest. Each avatar has a status dot: green (active, last_active ≤ 5m) or
 * amber (inactive, last_active > 5m or null).
 *
 * Hovering an avatar pulses that user's remote selection; clicking it toggles
 * follow-focus mode (jump to and track their selection).
 */
export const DataTableOnlineUsers: FC = () => {
  const { users } = useDataTable();
  const online = useOnlineUsers();
  const { pulseUserSelection, toggleFollowUser, followedUserId } = useRemoteSelectionInteraction();

  const { visible, overflow } = useMemo(() => {
    const resolved = resolveUsers(online, users);
    return {
      visible: resolved.slice(0, VISIBLE_LIMIT).reverse(),
      overflow: resolved.slice(VISIBLE_LIMIT),
    };
  }, [online, users]);

  if (visible.length === 0 && overflow.length === 0) {
    return null;
  }

  return (
    <div className={styles.cluster} data-testid="datatable-online-users">
      {overflow.length > 0 && <OverflowChip users={overflow} />}
      {visible.map((user) => (
        <UserPresenceChip
          key={user.apiId}
          userId={user.apiId}
          name={user.name}
          color={user.color}
          isActive={user.isActive}
          interactive
          following={followedUserId === user.apiId}
          testId={`datatable-online-users__chip--${user.apiId}`}
          onPulse={pulseUserSelection}
          onToggleFollow={toggleFollowUser}
        />
      ))}
    </div>
  );
};
